import { cn } from '@naumu/ui/utils/cn';

import type { MarketingNavPanelFooter, MarketingNavSubItem } from '../config/marketing-nav';
import {
	NavPanelCard,
	NavPanelCellLink,
	NavPanelFooterRow,
} from './marketing-nav-panel-card';

/**
 * NavColumnsPanel - the designed popover body for Use cases and Resources,
 * sharing the Product popover's exact anatomy (card-in-card, grey label +
 * white benefit cells, hairline column and row rules, footer row). Default
 * layout is column-major: each `group` (in declaration order) becomes one
 * column. An explicit `columnCount` flips to row-major instead - items fill
 * left-to-right across that many columns, so an N-item group reads as a row
 * (Use cases: companies on top, individuals below). Cells sit on an explicit
 * shared grid so rows align even when one benefit line wraps taller. No
 * visible group headers - the grouping does its work silently.
 */
export const NavColumnsPanel = ({
	items,
	footer,
	columnCount,
}: {
	items: readonly MarketingNavSubItem[];
	footer?: MarketingNavPanelFooter;
	columnCount?: number;
}) => {
	let totalColumns: number;
	let cells: { item: MarketingNavSubItem; column: number; row: number }[];
	if (columnCount) {
		totalColumns = columnCount;
		cells = items.map((item, index) => ({
			item,
			column: index % columnCount,
			row: Math.floor(index / columnCount),
		}));
	} else {
		// Split into columns by `group`, preserving declaration order.
		const columns: MarketingNavSubItem[][] = [];
		for (const item of items) {
			const last = columns[columns.length - 1];
			if (last && last[0].group === item.group) last.push(item);
			else columns.push([item]);
		}
		totalColumns = columns.length;
		cells = columns.flatMap((column, columnIndex) =>
			column.map((item, rowIndex) => ({ item, column: columnIndex, row: rowIndex })),
		);
	}

	return (
		<div className="p-2">
			<NavPanelCard>
				<div
					className="grid"
					style={{ gridTemplateColumns: `repeat(${totalColumns}, minmax(0, 1fr))` }}
				>
					{cells.map(({ item, column, row }) => (
						<NavPanelCellLink
							key={item.href}
							cell={{ label: item.label, benefit: item.description ?? '', href: item.href }}
							className={cn(
								'border-white/[0.06]',
								column > 0 && 'border-l',
								row > 0 && 'border-t',
							)}
							style={{ gridColumnStart: column + 1, gridRowStart: row + 1 }}
						/>
					))}
				</div>
			</NavPanelCard>

			{footer && (
				<NavPanelFooterRow
					note={footer.note}
					linkLabel={footer.linkLabel}
					linkHref={footer.linkHref}
				/>
			)}
		</div>
	);
};
